/** macOS-style "toggle sidebar" glyphs (panel with a left/right rail). The ONE
 *  icon family for every side-panel toggle in the app — app sidebar, document
 *  thumbnails, takeoff panels — so they all read as the same control. Renders
 *  with `currentColor` fill; height drives width via the source aspect ratio. */

import React from 'react';

const VB_W = 23.3887;
const VB_H = 17.9785;

function toPx(v: number | string | undefined): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.endsWith('px')) return parseFloat(v);
  return undefined;
}

export function SidebarLeftIcon({
  style,
  className,
}: {
  style?: React.CSSProperties;
  strokeWidth?: number | string;
  className?: string;
}) {
  const boxH = toPx(style?.height) ?? 17;
  const boxW = toPx(style?.width);
  // The glyph is wider than tall. When the caller gives both width and height
  // (the header pill's 17x17 icon box) fit it INSIDE that box so it matches the
  // neighbouring square icons instead of spilling wider. When only a height is
  // given (the sidebar trigger) keep the natural aspect-ratio width.
  let width: number;
  let height: number;
  if (boxW != null) {
    const scale = Math.min(boxW / VB_W, boxH / VB_H);
    width = VB_W * scale;
    height = VB_H * scale;
  } else {
    height = boxH;
    width = boxH * (VB_W / VB_H);
  }
  return (
    <svg
      viewBox="0 0 23.3887 17.9785"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      aria-hidden="true"
      style={{ ...style, width, height }}
      className={className}
    >
      <path d="M7.44141 16.7188L8.97461 16.7188L8.97461 1.28906L7.44141 1.28906ZM4.12109 17.9785L19.1504 17.9785C21.6113 17.9785 23.0273 16.4941 23.0273 13.8574L23.0273 4.13086C23.0273 1.49414 21.6113 0 19.1504 0L4.12109 0C1.49414 0 0 1.49414 0 4.13086L0 13.8574C0 16.4941 1.49414 17.9785 4.12109 17.9785ZM4.13086 16.4062C2.50977 16.4062 1.57227 15.4785 1.57227 13.8574L1.57227 4.13086C1.57227 2.50977 2.50977 1.57227 4.13086 1.57227L18.8965 1.57227C20.5176 1.57227 21.4551 2.50977 21.4551 4.13086L21.4551 13.8574C21.4551 15.4785 20.5176 16.4062 18.8965 16.4062ZM5.56641 5.20508C5.85938 5.20508 6.12305 4.94141 6.12305 4.6582C6.12305 4.36523 5.85938 4.11133 5.56641 4.11133L3.4668 4.11133C3.17383 4.11133 2.91992 4.36523 2.91992 4.6582C2.91992 4.94141 3.17383 5.20508 3.4668 5.20508ZM5.56641 7.73438C5.85938 7.73438 6.12305 7.4707 6.12305 7.17773C6.12305 6.88477 5.85938 6.64062 5.56641 6.64062L3.4668 6.64062C3.17383 6.64062 2.91992 6.88477 2.91992 7.17773C2.91992 7.4707 3.17383 7.73438 3.4668 7.73438ZM5.56641 10.2539C5.85938 10.2539 6.12305 10.0098 6.12305 9.7168C6.12305 9.42383 5.85938 9.16992 5.56641 9.16992L3.4668 9.16992C3.17383 9.16992 2.91992 9.42383 2.91992 9.7168C2.91992 10.0098 3.17383 10.2539 3.4668 10.2539Z" />
    </svg>
  );
}

/** Mirrored variant for RIGHT-side panels (Apple's sidebar.right). */
export function SidebarRightIcon(props: {
  style?: React.CSSProperties;
  strokeWidth?: number | string;
  className?: string;
}) {
  return (
    <span style={{ display: 'inline-flex', transform: 'scaleX(-1)' }} aria-hidden="true">
      <SidebarLeftIcon {...props} />
    </span>
  );
}
